import '../../DatePicker.scss';
import { isPast, isToday } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  getEndOfPrevMonth,
  getExtraDays,
  getMonthAndYear,
  getMonthDays,
} from '../../DatePickerUtils/DatePickerUtils';
import SvgIcon from '../../../SvgIcon/SvgIcon';
import { getDateDropdownClassList } from './DateDropdownUtils/DateDropdownUtils';
import Text from '../../../Text/Text';

const DateDropdown = ({
  date,
  setDate,
  isDropdownOpen,
  toggleDropdown,
  showTodayTomorrow,
  setPlaceholder,
  showPastDates,
}: {
  date: Date;
  setDate: (v: Date) => void;
  isDropdownOpen: boolean;
  toggleDropdown: () => void;
  showTodayTomorrow: boolean;
  setPlaceholder: (v: string) => void;
  showPastDates: boolean;
}) => {
  const [localDate, setLocalDate] = useState({
    day: date.getDate(),
    month: date.getMonth(),
    year: date.getFullYear(),
  });
  const localMonthDays = getMonthDays(localDate.month, localDate.year);
  const { days, firstDayOfWeek } = localMonthDays;

  const handlePrevMonth = () => {
    if (localDate.month === 0) {
      setLocalDate(prevDate => ({
        ...prevDate,
        year: prevDate.year - 1,
        month: 11,
        day: 1,
      }));
    } else {
      setLocalDate(prevDate => ({
        ...prevDate,
        day: 1,
        month: prevDate.month - 1,
      }));
    }
  };

  const handleNextMonth = () => {
    if (localDate.month === 11) {
      setLocalDate(prevDate => ({
        ...prevDate,
        month: 0,
        day: 1,
        year: prevDate.year + 1,
      }));
    } else {
      setLocalDate(prevDate => ({
        ...prevDate,
        day: 1,
        month: prevDate.month + 1,
      }));
    }
  };

  const handleDropdownToggle = () => {
    setPlaceholder('');
    toggleDropdown();
  };

  const handleDateValidity = (day: Date) => {
    if (!showPastDates && isPast(day) && !isToday(day)) return;

    setDate(day);
    handleDropdownToggle();
  };

  const previousMonthArrowColour = useMemo(() => {
    const today = new Date();

    if (showPastDates) return 'var(--black)';
    if (today.getMonth() !== 11 && today.getMonth() < localDate.month) return 'var(--black)';
    if (localDate.year > today.getFullYear()) return 'var(--black)';
    return 'var(--grey400)';
  }, [localDate.month, localDate.year, showPastDates]);

  const generateDayClassName = (day: Date) => {
    const dayIsEqual = day.getDate() === date.getDate();
    const monthIsEqual = day.getMonth() === date.getMonth();
    const yearIsEqual = day.getFullYear() === date.getFullYear();
    const classList = ['date-square', 'body-small'];

    // highlight the selected day
    if (dayIsEqual && monthIsEqual && yearIsEqual) classList.push('callout', 'selected');

    // disable days in current month that are in the past
    if (!showPastDates && isPast(day) && !isToday(day)) classList.push('disabled');

    return classList.join(' ');
  };

  const generateGridSquares = () => {
    // Add squares for days in current month
    const daysInMonth = days.map((day, index) => {
      const date = day.getDate();
      return (
        <button
          data-testid={`date-selection-square-${date}`}
          key={`${date}-${index}`}
          type="button"
          className={generateDayClassName(day)}
          onClick={() => handleDateValidity(day)}
        >
          {date}
        </button>
      );
    });

    // Add squares for days before the 1st day of the month
    const prevMonthDays = getEndOfPrevMonth(
      1,
      localDate.month === 0 ? 11 : localDate.month - 1,
      localDate.month === 0 ? localDate.year - 1 : localDate.year,
    );
    let prevMonthDaysCount = 0;
    const firstDayOfWeekIndex = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 1; i <= firstDayOfWeekIndex; i++) {
      daysInMonth.unshift(
        <button
          key={`prev-month-${i}`}
          type="button"
          className={`date-square body-small disabled prev-month`}
          disabled
        >
          <Text size="small" type="body">
            {prevMonthDays[prevMonthDays.length - i].getDate().toString()}
          </Text>
        </button>,
      );
      prevMonthDaysCount += 1;
    }

    // Add squares for days after the last day of the month
    const extraDays = getExtraDays(days, firstDayOfWeekIndex, prevMonthDaysCount);
    for (let i = 1; i <= extraDays; i++) {
      daysInMonth.push(
        <button
          key={`next-month-${i}`}
          type="button"
          className={`date-square body-small disabled next-month`}
          disabled
        >
          <Text size="small" type="body">
            {i.toString()}
          </Text>
        </button>,
      );
    }

    return daysInMonth;
  };

  return (
    <div className={getDateDropdownClassList(isDropdownOpen, showTodayTomorrow)} role="listbox">
      <div className="date-navigation">
        <div className="prev-next-months">
          <button
            id="left-nav-button"
            type="button"
            data-testid="left-nav-button"
            className="left-right-button"
            onClick={handlePrevMonth}
            disabled={!showPastDates && previousMonthArrowColour === 'var(--grey400)'}
          >
            <SvgIcon
              iconName="ChevronLeft"
              fill={previousMonthArrowColour}
              width={12}
              height={12}
            />
          </button>
          <span className="body-small">
            <Text size="small" type="body">
              {getMonthAndYear(localDate.day, localDate.month, localDate.year)}
            </Text>
          </span>
          <button
            id="right-nav-button"
            type="button"
            data-testid="right-nav-button"
            className="left-right-button"
            onClick={handleNextMonth}
          >
            <SvgIcon iconName="ChevronRight" fill={'var(--black)'} width={12} height={12} />
          </button>
        </div>
      </div>
      <div className="date-grid">
        <div className="date-grid-header">
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Mo
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Tu
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              We
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Th
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Fr
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Sa
            </Text>
          </span>
          <span className="body-small date-header-square">
            <Text size="small" type="body">
              Su
            </Text>
          </span>
        </div>
        <div className="date-grid-body">{generateGridSquares()}</div>
      </div>
    </div>
  );
};

export default DateDropdown;
